/**
 * Field guide for the Event Module form. Each entry describes what a field is
 * for, gives a short example value, and lists the UI surfaces where its value
 * actually appears.
 *
 * Annotate screenshots manually (red oval / arrow) before saving them — the
 * lightbox no longer draws programmatic hotspots.
 *
 * To add real screenshots: drop PNG/JPG files into
 * `public/images/event-module-guide/` and point `img` at them.
 */

const PLACEHOLDER = "/images/event-module-guide/placeholder.svg";

export const fieldGuide = {
  // ----- Basic Info -----
  serviceKey: {
    label: "Service Key",
    purpose:
      "Unique technical identifier for the module. Used internally to derive route prefixes, store namespaces, and API paths. Cannot be changed after creation.",
    example: "hrdc_einvoicing",
    displays: [
      {
        img: PLACEHOLDER,
        caption: "Internal — derives route prefix, store name, and URL paths",
      },
    ],
  },

  hrdcType: {
    label: "HRDC Type",
    purpose:
      "Auto-assigned numeric code that orders modules in dropdowns and routing logic. Generated from the total number of existing modules.",
    example: "5",
    displays: [
      {
        img: PLACEHOLDER,
        caption: "Internal ordering — auto-assigned",
      },
    ],
  },

  appName: {
    label: "App Name",
    purpose:
      "Full application name shown across the console: as the card title and description on the Console dashboard, in the navbar service-button label and services dropdown, as the H1 header on the Service dashboard, and prefixed onto page headers like \"{App Name} Applications\".",
    example: "HRDC e-Invoicing",
    displays: [
      {
        img: "/images/event-module-guide/basic-info-app-name.png",
        caption:
          "Console dashboard — service card title and \"Manage {App Name} Application\" description",
      },
    ],
  },

  icon: {
    label: "Icon",
    purpose:
      "Material Design icon shown on the module's dashboard card and side-menu entry.",
    displays: [
      {
        img: "/images/event-module-guide/basic-info-icon.png",
        caption: "Dashboard card icon",
      },
    ],
  },

  accentColor: {
    label: "Accent Color",
    purpose:
      "Primary color used for the module's dashboard card, header bar, and accent UI elements.",
    displays: [
      {
        img: "/images/event-module-guide/basic-info-accent-color.png",
        caption: "Dashboard card accent border / background",
      },
    ],
  },

  name1: {
    label: "Name 1 (Short)",
    purpose:
      "Short, friendly module name. Used as the prefix in the email subject on the Send HRDC Email To Client page (the entire subject line is automatically converted to UPPERCASE before sending), in refund Excel/ZIP filenames, and as the basis for the auto-derived CRM view key.",
    example: "E-Invoicing",
    displays: [
      {
        img: "/images/event-module-guide/basic-info-name1.png",
        caption:
          "Email Subject prefix on Send HRDC Email To Client — auto-uppercased before sending",
      },
    ],
  },

  // ----- Documents -----
  confirmationLetterTemplate: {
    label: "Confirmation Letter",
    purpose:
      "Filename of the Confirmation Letter PDF. Auto-generated from Name 1. The PDF Config tab uses this to know which file's field positions to edit, and the same PDF is attached to outgoing client emails.",
    example: "CONFIRMATION_LETTER_E_INVOICING_TEMPLATE",
  },

  jd14Template: {
    label: "JD14 Template",
    purpose:
      "Filename of the JD14 grant application PDF. Auto-generated from Name 1. The PDF Config tab uses this to know which file's field positions to edit, and the same PDF is attached to outgoing client emails.",
    example: "JD14_E_INVOICING_TEMPLATE",
  },

  t3Template: {
    label: "T3 Template",
    purpose:
      "Filename of the T3 claim form PDF. Auto-generated from Name 1. The PDF Config tab uses this to know which file's field positions to edit, and the same PDF is attached to outgoing client emails.",
    example: "T3_E_INVOICING_TEMPLATE",
  },

  agendaAttachmentName: {
    label: "Agenda Attachment Name",
    purpose:
      "Display filename for the Course Content / Agenda PDF attachment. Used both as the filename when downloaded and as the label shown in the Attachments tab. The actual PDF file is uploaded under the Attachments tab.",
    example: "YYC e-Invoicing Conference Course Content",
    displays: [
      {
        img: "/images/event-module-guide/documents-agenda-attachment-name.png",
        caption: "Agenda PDF filename when attached to outgoing emails",
      },
    ],
  },

  guidelineName: {
    label: "Guideline Name",
    purpose:
      "Display filename for the HRDC Grant & Claim Application Procedure (Guideline) PDF. Used both as the filename when downloaded and as the label shown in the Attachments tab. The actual PDF file is uploaded under the Attachments tab.",
    example:
      "YYC E - INVOICING CONFERENCE - HRDC Grant and Claim Application Procedure",
    displays: [
      {
        img: "/images/event-module-guide/documents-guideline-name.png",
        caption: "Guideline PDF filename when attached to outgoing emails",
      },
    ],
  },

  // ----- Pricing -----
  memberPricing: {
    label: "taxPOD Member Pricing",
    purpose:
      "Display-only color indicator for the Application list. The price field is free text on the application form — applicants type any amount they want. If the amount they enter matches a price configured here, the Price column in the Application list renders it in the color you've picked beside that price. Non-matching amounts show with no color highlight.",
    example: "RM 2,600 (blue)",
    displays: [
      {
        img: "/images/event-module-guide/pricing.png",
        caption:
          "Application list — Price column color-matches entries against this list",
      },
    ],
  },

  nonMemberPricing: {
    label: "Non-taxPOD Member Pricing",
    purpose:
      "Display-only color indicator for the Application list. Same as the Member section but for non-taxPOD-member registrants. The price entered on the application form is free text; if it matches an amount configured here, it renders in the matching color.",
    example: "RM 1,800 (green)",
    displays: [
      {
        img: "/images/event-module-guide/pricing.png",
        caption:
          "Application list — Price column color-matches entries against this list",
      },
    ],
  },

  isSingleDay: {
    label: "Single Day Event",
    purpose:
      "Controls how the event date renders in the HRDC client email body. Tick for single-day workshops (renders as \"5th of November 2024\") or leave unticked for multi-day events (renders as \"5th & 7th of November 2024\"). Both formats substitute into the {{eventDate}} token of the email template.",
    example: "Single-day workshop → ticked; 2-day conference → unticked",
    displays: [
      {
        img: "/images/event-module-guide/basic-info-single-day-event.png",
        caption:
          "HRDC client email body — event date format depends on this checkbox",
      },
    ],
  },

  hasAttendanceList: {
    label: "Has Attendance List",
    purpose:
      "Marks this module as requiring an attendance list submission. When ticked, the Application list table shows an additional \"Attendance List\" status badge per row, and the revert/claim flows surface attendance-list upload steps. Leave unticked if attendance is not separately tracked.",
    example: "TPDIY → ticked; e-Invoicing / Stamp Duty → unticked",
    displays: [
      {
        img: "/images/event-module-guide/basic-info-has-attendance-list.png",
        caption:
          "Application list — extra \"Attendance List\" status badge appears when ticked",
      },
    ],
  },

  // ----- Settings -----
  crmViewKey: {
    label: "Table View Key",
    purpose: "Backend lookup key for the module's Application list view.",
    example: "e_invoicing_application",
  },

  xeroCallbackSuffix: {
    label: "Xero Callback Suffix",
    purpose: "Backend identifier for the module's Xero OAuth callback URL.",
    example: "callback_e_invoicing",
  },

  hrdcGrantProgrammeNamePattern: {
    label: "HRDC Grant Programme Name",
    purpose:
      "Must match the Programme Name registered in the eTRIS system exactly. Used to verify uploaded HRDC Grant Approval Emails — the text after \"PROGRAMME NAME:\" in the email is matched against this pattern. If it doesn't match, the grant email is rejected.",
    example: "E- Invoicing Conference",
    displays: [
      {
        img: "/images/event-module-guide/settings-etris-programme-name.png",
        caption:
          "eTRIS — Programme Name registration page (must match this value verbatim)",
      },
    ],
  },
};

export function getFieldGuide(key) {
  return fieldGuide[key] || null;
}
